import {
  GOOGLE_CLASSROOM_DEFAULT_TERM_YEAR,
  GOOGLE_HANGOUT_DEFAULT_TERM_YEAR,
} from './../../../shared/constants/remote-learning.constant';
import { IBoxInfo } from './../../shared-components/info-box/info-box.component';
import { get, orderBy } from 'lodash';
import { IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import { TValidStoredPanels } from './../student-panel.service';
import { StudentPanelService } from '../student-panel.service';
import { Component, Input, Inject } from '@angular/core';
import StudentPanelsConfig from '../../student-container/student-panels.config';
import { ICourseRow, IAssignment, IAssignmentRow, ICourse, ITotalRow, IInfoBoxConfig } from './models';

const { human: panelTitle, id } = StudentPanelsConfig.REMOTE_LEARNING;

@Component({
  selector: 'remote-learning-panel',
  template: require('./remote-learning-panel.component.html'),
  styles: [require('./remote-learning-panel.component.scss')],
})
export class RemoteLearningPanelComponent {
  @Input() collapsed: boolean;
  @Input() student: IStudent;

  private googleHangoutTermYear: string;
  public googleClassroomTermYear: string;

  private InfoBoxConfigs: Array<IInfoBoxConfig>;

  public panelTitle: string = panelTitle;
  public infoBoxes: Array<IBoxInfo>;
  public courseRows: Array<ICourseRow>;
  public totalRow: { totalAssignments: number; totalEngagedAssignments: number };

  constructor(private panelService: StudentPanelService, @Inject('$window') private $window) {}

  ngOnInit(): void {
    this.googleClassroomTermYear = this.getTermYear(this.student.googleClassroom) || GOOGLE_CLASSROOM_DEFAULT_TERM_YEAR;
    this.googleHangoutTermYear = this.getTermYear(this.student.googleHangout) || GOOGLE_HANGOUT_DEFAULT_TERM_YEAR;

    // This was moved here because the template literals weren't being set correctly (Alex)
    this.InfoBoxConfigs = this.setDefaultInfoBoxConfigs();

    this.courseRows = this.student.googleClassroom ? orderBy(this.getCourseRows(this.student), ['name']) : [];
    this.totalRow = this.student.googleClassroom ? this.getTotalRow(this.student) : null;
    this.collapsed = this.panelService.getInitialPanelState(id as TValidStoredPanels);
    this.infoBoxes = this.InfoBoxConfigs.map(this.getBoxInfo.bind(this, this.student));
  }

  public togglePanelBody(): void {
    this.collapsed = this.panelService.togglePanelBody(this.collapsed, this.panelTitle);
  }

  public toggleRowExpandedProp(courseRow: ICourseRow): void {
    courseRow.expanded = !courseRow.expanded;
  }

  public openFileInNewTab(driveFileId: string): void {
    if (!driveFileId) return;
    this.$window.open(`https://drive.google.com/open?id=${driveFileId}`, '_blank');
  }

  private formatPercentage(float: number): string {
    return `${Math.round(float * 100)}%`;
  }

  private formatAbsences(totalDays: number) {
    return (attendedDays: number) => {
      return `${totalDays - attendedDays}`;
    };
  }

  private setDefaultInfoBoxConfigs(): Array<IInfoBoxConfig> {
    return [
      {
        title: `${this.googleHangoutTermYear || GOOGLE_HANGOUT_DEFAULT_TERM_YEAR} Google Hangouts Engagement`,
        path: 'googleHangout.remoteEngagement',
        formatFn: this.formatPercentage,
      },
      {
        title: `${this.googleClassroomTermYear || GOOGLE_CLASSROOM_DEFAULT_TERM_YEAR} Google Classroom Engagement`,
        path: 'googleClassroom.remoteEngagement',
        formatFn: this.formatPercentage,
      },
    ];
  }

  private getBoxInfo(student: IStudent, config: { title: string; path: string; formatFn: Function }): IBoxInfo {
    const val = get(student, config.path);
    const formattedData = val !== null && val !== undefined ? config.formatFn(val) : null;
    return {
      title: config.title,
      data: formattedData,
    };
  }

  private getTotalRow(student: IStudent): ITotalRow {
    const {
      googleClassroom: { totalAssignments, totalEngagedAssignments },
    } = student;
    return { totalAssignments, totalEngagedAssignments };
  }

  private getCourseRows(student: IStudent): Array<ICourseRow> {
    const {
      googleClassroom: { courses },
    } = student;
    return courses.map(this.convertCourseToRow.bind(this));
  }

  private getMostRecentSubmittedTooltip(mostRecentSubmittedAssignment: string): string {
    if (!mostRecentSubmittedAssignment) return null;
    return `Most recent submission on ${this.formatDate(mostRecentSubmittedAssignment, {
      month: 'MMM',
      day: 'DD,',
      year: 'YYYY',
    })}`;
  }

  private convertCourseToRow({
    courseId,
    name,
    teacherName,
    assignmentsGiven,
    assignmentsTurnedIn,
    assignments,
    mostRecentSubmittedAssignment,
  }: ICourse): ICourseRow {
    return {
      name,
      courseId,
      teacherName,
      assignmentsGiven,
      assignmentsTurnedIn,
      mostRecentSubmittedAssignment: this.getMostRecentSubmittedTooltip(mostRecentSubmittedAssignment),
      assignments: assignments
        .sort(this.sortAssignments)
        .slice(0, 10)
        .map(this.convertAssignmentToRow.bind(this)),
      expanded: false,
    };
  }

  private convertAssignmentToRow({ assignmentName, actionDate, driveFileId }: IAssignment): IAssignmentRow {
    // In the future, if we need more flexibility, we can pass this config in
    const formatConfig = { month: 'MMM', day: 'DD,', year: 'YYYY' };
    return {
      assignmentName,
      actionDate: this.formatDate(actionDate, formatConfig),
      driveFileId,
    };
  }

  private sortAssignments(
    { actionDate: assignment1Date }: IAssignment,
    { actionDate: assignment2Date }: IAssignment,
  ): number {
    const sortFormat = 'YYYYMMDD';
    const formattedDate1 = new moment(assignment1Date).format(sortFormat);
    const formattedDate2 = new moment(assignment2Date).format(sortFormat);
    return formattedDate2 - formattedDate1;
  }

  private formatDate(dateString: string, config: { month: string; day: string; year: string }): string {
    const { month, day, year } = config;
    return moment(dateString).format(`${month} ${day} ${year}`);
  }

  private getTermYear({
    schoolYear,
    termYearDescription,
  }: IStudent['googleClassroom'] | IStudent['googleHangout']): string {
    // We don't have google hangout or classroom data for the summer term
    // If this changes, then update accordingly
    if (!schoolYear || !termYearDescription || termYearDescription === 'Summer') return;
    const formattedSchoolYear = schoolYear.slice(0, 2) + schoolYear.slice(-5); // Convert school year to year (Ex: SY2019-20 => SY19-20)
    const termYear = `${termYearDescription} ${formattedSchoolYear}`; // ie. "Spring 2020"
    return termYear;
  }
}
